import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown, MousePointerClick, SlidersHorizontal, Flame, Building2, X } from "lucide-react";
import { useSim } from "../lib/store";
import { xrStore } from "../three/xrStore";

const KEY = "tab_onboarded_v1";

export function Onboarding() {
  const events = useSim((s) => s.events);
  const magnitude = useSim((s) => s.magnitude);
  const duration = useSim((s) => s.duration);
  const viewMode = useSim((s) => s.viewMode);
  const introPlaying = useSim((s) => s.introPlaying);
  const tourActive = useSim((s) => s.tourActive);

  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const initRef = useRef({ mag: magnitude, dur: duration });

  // Device-specific interaction verb: Click (desktop) / Tap (touch) / Pinch/Grab (VR headset).
  const [verb, setVerb] = useState(() => {
    if (typeof window === "undefined") return "Click";
    return window.matchMedia && window.matchMedia("(pointer: coarse)").matches ? "Tap" : "Click";
  });
  useEffect(() => {
    const update = () => {
      const session = xrStore.getState?.().session;
      if (session) setVerb("Pinch/Grab");
      else setVerb(window.matchMedia && window.matchMedia("(pointer: coarse)").matches ? "Tap" : "Click");
    };
    update();
    const unsub = xrStore.subscribe ? xrStore.subscribe(update) : undefined;
    return () => { if (unsub) unsub(); };
  }, []);

  const STEPS = useMemo(
    () => [
      {
        icon: MousePointerClick,
        title: "Drop an Epicenter",
        body: `${verb} the globe to place a gold draft pin, then ${verb.toLowerCase()} again to lock it red.`,
        pos: "top",
        done: (st) => st.events.length >= 1,
      },
      {
        icon: SlidersHorizontal,
        title: "Tune the Rupture",
        body: "Adjust Moment Magnitude & Rupture Duration to shape the quake's energy.",
        pos: "deck",
        done: (st, init) => st.magnitude !== init.mag || st.duration !== init.dur,
      },
      {
        icon: Flame,
        title: "Add a Volcanic Event",
        body: `${verb} again near a glowing orange volcano to stage a second event (up to 3 run together).`,
        pos: "top",
        done: (st) => st.events.length >= 2,
      },
      {
        icon: Building2,
        title: "Enter the 1:1 City",
        body: `${verb} “1:1 Sandbox” in the controls to drop into the street-level metropolis.`,
        pos: "deck",
        done: (st) => st.viewMode === "street",
      },
    ],
    [verb]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.localStorage.getItem(KEY)) setActive(true);
  }, []);

  useEffect(() => {
    if (step === 1) initRef.current = { mag: magnitude, dur: duration };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const finish = () => {
    if (typeof window !== "undefined") window.localStorage.setItem(KEY, "1");
    setActive(false);
  };

  // mirror the current step to the store so a VR head-locked slate can display it
  useEffect(() => {
    const s = STEPS[step];
    useSim.getState().setOnboard({
      onboardActive: active && !introPlaying && !tourActive,
      onboardTitle: s ? s.title : "",
      onboardBody: s ? s.body : "",
    });
  }, [active, introPlaying, tourActive, step, STEPS]);

  useEffect(() => {
    if (!active || introPlaying || tourActive) return;
    const st = { events, magnitude, duration, viewMode };
    if (STEPS[step].done(st, initRef.current)) {
      const tm = setTimeout(() => {
        if (step + 1 < STEPS.length) setStep((s) => s + 1);
        else finish();
      }, 650);
      return () => clearTimeout(tm);
    }
  }, [active, introPlaying, tourActive, events, magnitude, duration, viewMode, step, STEPS]);

  if (!active || introPlaying || tourActive) return null;

  const s = STEPS[step];
  const Icon = s.icon;
  const onTop = s.pos === "top";

  const wrapPos = onTop
    ? "top-24 left-1/2 -translate-x-1/2 items-center"
    : "bottom-24 right-4 sm:right-6 lg:right-[476px] items-end";

  return (
    <div className="fixed inset-0 z-[80] pointer-events-none">
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          data-testid="onboarding-card"
          initial={{ opacity: 0, y: onTop ? -16 : 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: onTop ? -12 : 12 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className={`absolute flex flex-col gap-2 ${wrapPos}`}
        >
          <div className="pointer-events-auto w-[86vw] max-w-sm p-4 rounded-2xl bg-zinc-950/90 backdrop-blur-2xl border border-[rgba(201,169,74,0.45)] shadow-[0_20px_60px_rgba(0,0,0,0.9),0_0_30px_rgba(201,169,74,0.12)]">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[rgba(201,169,74,0.15)] border border-[rgba(201,169,74,0.4)]">
                  <Icon className="h-4 w-4 text-[#c9a94a]" />
                </span>
                <div>
                  <div className="font-mono-tele text-[10px] uppercase tracking-[0.2em] text-[#c9a94a]">Step {step + 1} / {STEPS.length}</div>
                  <h3 style={{ fontFamily: 'Georgia, serif' }} className="text-base font-semibold text-[#f4ede0] leading-tight">{s.title}</h3>
                </div>
              </div>
              <button
                data-testid="onboarding-skip"
                onClick={finish}
                title="Skip tutorial"
                className="shrink-0 flex items-center gap-1 h-8 px-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white text-[11px] font-medium transition-colors"
              >
                <X className="h-3.5 w-3.5" /> Skip
              </button>
            </div>
            <p className="mt-2.5 text-sm leading-relaxed text-zinc-300">{s.body}</p>
            <div className="mt-3 flex items-center gap-1.5">
              {STEPS.map((_, i) => (
                <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-[#c9a94a]" : i < step ? "w-1.5 bg-[#c9a94a]/50" : "w-1.5 bg-white/20"}`} />
              ))}
            </div>
          </div>

          {/* gold glowing arrow pointing at the relevant control */}
          <motion.div
            animate={{ y: onTop ? [0, 10, 0] : [0, -10, 0] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
            className={onTop ? "self-center" : "self-end mr-8"}
          >
            <ArrowDown
              className="h-8 w-8 text-[#c9a94a]"
              style={{ filter: "drop-shadow(0 0 10px rgba(201,169,74,0.9))", transform: onTop ? "none" : "rotate(-25deg)" }}
            />
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
